/**
 * YouTube — normaliza o link que o trainer cola e monta as URLs de embed/thumb.
 *
 * Por que existe: o exercicio ja tem `video_url` (beta_student_workout_items),
 * e o portal ja sabia tocar arquivo de video (<video src>). Mas link do YouTube
 * NAO toca em <video> — precisa de <iframe> de embed. Este helper decide.
 *
 * O trainer vai colar QUALQUER formato — principalmente **Shorts** (video
 * vertical, igual GIF de exercicio). Aceitamos todos e extraimos o videoId.
 *
 * Embed via `youtube-nocookie.com`: o portal do aluno nao deve injetar cookie
 * de tracking do Google em quem so quer ver o exercicio.
 */

/** videoId do YouTube: 11 chars [A-Za-z0-9_-]. */
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

const YT_HOSTS = new Set(['youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'music.youtube.com']);
/** Segmentos de path que precedem o id: /shorts/<id>, /embed/<id>, /live/<id>, /v/<id>. */
const PATH_PREFIXES = new Set(['shorts', 'embed', 'live', 'v']);

/**
 * Extrai o videoId de qualquer formato de link do YouTube (watch?v=, youtu.be/,
 * shorts/, embed/, live/), ignorando params extras (timestamp, playlist, si=).
 * Aceita tambem o id cru colado direto. Retorna null se nao for YouTube.
 */
export function parseYouTubeId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Id cru colado direto (ex: "dQw4w9WgXcQ").
  if (VIDEO_ID_RE.test(trimmed)) return trimmed;

  // Sem protocolo (ex: "youtu.be/xxx") o URL() quebra — tenta com https.
  let u: URL;
  try {
    u = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }

  const host = u.hostname.replace(/^www\./i, '').toLowerCase();

  // youtu.be/<id>
  if (host === 'youtu.be') {
    const id = u.pathname.split('/').filter(Boolean)[0];
    return id && VIDEO_ID_RE.test(id) ? id : null;
  }

  if (!YT_HOSTS.has(host)) return null;

  // youtube.com/watch?v=<id>
  const v = u.searchParams.get('v');
  if (v && VIDEO_ID_RE.test(v)) return v;

  // youtube.com/shorts/<id> | /embed/<id> | /live/<id> | /v/<id>
  const parts = u.pathname.split('/').filter(Boolean);
  if (parts.length >= 2 && PATH_PREFIXES.has(parts[0].toLowerCase())) {
    const id = parts[1];
    return VIDEO_ID_RE.test(id) ? id : null;
  }

  return null;
}

/** True se a URL e um link do YouTube reconhecivel. */
export function isYouTubeUrl(raw: string | null | undefined): boolean {
  return parseYouTubeId(raw) !== null;
}

/**
 * URL de embed pro <iframe>. `rel=0` evita sugerir video de terceiro no fim
 * (o aluno nao deve sair pro YouTube alheio); `playsinline=1` toca dentro da
 * pagina no iOS em vez de abrir fullscreen nativo.
 */
export function youTubeEmbedUrl(raw: string | null | undefined): string | null {
  const id = parseYouTubeId(raw);
  if (!id) return null;
  return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`;
}

/**
 * Thumbnail do video — a miniatura do exercicio usa <img>, porque <video> nao
 * consegue renderizar um link do YouTube. hqdefault existe pra todo video
 * (maxres nem sempre).
 */
export function youTubeThumbUrl(raw: string | null | undefined): string | null {
  const id = parseYouTubeId(raw);
  if (!id) return null;
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}
